import type { PaymentCycle } from "@prisma/client";

import dayjs from "@calcom/dayjs";

export const formatPhoneNumber = (phoneNumber: string) => {
  const phone = phoneNumber.replace(/\D/g, "").slice(0, 10);
  if (phone.length >= 7) {
    return `(${phone.slice(0, 3)})${phone.slice(3, 6)}-${phone.slice(6)}`;
  } else if (phone.length >= 4) {
    return `(${phone.slice(0, 3)})${phone.slice(3)}`;
  }
  return phone;
};

export const validatePhoneNumber = (phoneNumber: string) => {
  const phone = phoneNumber.replace(/\D/g, "").slice(0, 10);
  if (phone.length >= 10) {
    return true;
  } else return false;
};

export const formatBirthDate = (birthday: string) => {
  const birtDate = birthday.replace(/\D/g, "").slice(0, 8);
  if (birtDate.length >= 5) {
    return `${birtDate.slice(0, 2)}/${birtDate.slice(2, 4)}/${birtDate.slice(4)}`;
  } else if (birtDate.length >= 3) {
    return `${birtDate.slice(0, 2)}/${birtDate.slice(2)}`;
  }
  return birtDate;
};

export const ReadFileToBase64 = (file: File) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });
};

export const formatIntNumber = (number: number | undefined | null) => {
  if (number) {
    const formattedNumber = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return formattedNumber;
  } else {
    return 0;
  }
};

export const formatNumber = (number: number | undefined | null, type?: "balance" | "number") => {
  if (number) {
    if (type === "number") {
      return new Intl.NumberFormat("en-US").format(number);
    } else {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(number);
    }
  } else {
    return type == "number" ? 0 : "0.00";
  }
};

const INT4_MAX = 2147483647;

export const getValidPatientId = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  const absValue = Math.abs(Math.trunc(value));
  return absValue % 100000000;
};

/** Fits PMS/NexHealth ids into Postgres INT4 used by InsuranceClaim.claimId. */
export const getValidClaimId = (value: number | string | null | undefined): number => {
  const num = typeof value === "string" ? Number(value) : value ?? NaN;
  if (!Number.isFinite(num)) return 0;
  const absValue = Math.abs(Math.trunc(num));
  return absValue <= INT4_MAX ? absValue : absValue % 100000000;
};

/** Formats a value to exactly 2 decimal places (e.g. 10 → "10.00", 10.5 → "10.50"). */
export const formatAmountToTwoDecimals = (value: string | number): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (value === "" || value === undefined || isNaN(num)) return "0.00";
  return num.toFixed(2);
};

export const formatTimezone = (timezone: string) => {
  if (timezone) {
    const city = timezone.split("/").pop();
    return city?.replace("_", " ");
  }
};

export const getPaymentDate = (firstDate: Date, count: number, period: PaymentCycle) => {
  let unit: dayjs.ManipulateType;

  switch (period) {
    case "WEEKLY":
      unit = "week";
      break;
    case "BIWEEKLY":
      unit = "week";
      break;
    case "MONTHLY":
      unit = "month";
      break;
    case "YEARLY":
      unit = "year";
      break;
    default:
      unit = "day";
      break;
  }

  return dayjs(firstDate)
    .add(period == "BIWEEKLY" ? count * 2 : count, unit)
    .toDate();
};

export const getCategoryFromCode = (code: string) => {
  const mainCategory = code.slice(0, 2);
  const subCategory = +code.charAt(2);

  switch (mainCategory) {
    case "D0":
      return "Diagnostic";
    case "D1":
      return "Preventive";
    case "D2":
      return "Restorative";
    case "D3":
      return "Endodontics";
    case "D4":
      return "Periodontics";
    case "D5":
      return subCategory < 9 ? "Prosthodontics-removable" : "Maxillofacial Prosthodontics";
    case "D6":
      return subCategory < 2 ? "Implant Services" : "Prosthodontics, fixed";
    case "D7":
      return "Oral & Maxillofacial Surgery";
    case "D8":
      return "Orthodontics";
    case "D9":
      return "Adjunctive General Services";
    default:
      return code;
  }
};

export const calculateAgingBucket = (dpd?: number, useApptForLastVisitDate?: boolean) => {
  if (dpd === undefined) return "N/A";

  if (!useApptForLastVisitDate) {
    if (dpd < 30) return "0-30 days";
    else if (dpd < 60) return "30-60 days";
    else if (dpd < 90) return "60-90 days";
    else return "90+";
  }

  if (dpd < 30) return "<30";
  else if (dpd < 60) return "30-60";
  else if (dpd < 90) return "60-90";
  else if (dpd < 120) return "90-120";
  else if (dpd < 150) return "120-150";
  else if (dpd < 180) return "150-180";
  else if (dpd < 360) return "180-360";
  else if (dpd < 720) return "360-720";
  else return "720+";
};

const allowedKeys = /^[a-zA-Z0-9\s.,!?:;/@&$()\-=]$/;
const specialKeys = [
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "Tab",
  "Enter",
];

/**
 * Filters out special characters from text, keeping only alphanumeric, spaces, and basic punctuation
 * @param text - The text to filter
 * @returns Filtered text with only allowed characters
 */
export const filterMessageSpecialCharacters = (text: string): string => {
  return text.replace(/[^a-zA-Z0-9\s.,!?:;/@&$()\-=]/g, "");
};

/**
 * Creates an onKeyDown handler that prevents special characters from being typed
 * @param e - The keyboard event
 */
export const handleMessageKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // Allow alphanumeric, space, and basic punctuation
  // Allow special keys like Backspace, Delete, Arrow keys, etc.
  if (!specialKeys.includes(e.key) && !e.ctrlKey && !e.metaKey && !allowedKeys.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Creates an onPaste handler that filters pasted content to remove special characters
 * @param e - The paste event
 * @param setValue - Function to update the value (for react-hook-form: UseFieldArrayReturn['setValue'] or UseFormReturn['setValue'])
 * @param fieldName - The field name to update (for react-hook-form)
 */
export const handleMessagePaste = (
  e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  setValue?: (name: string, value: string) => void | ((value: string) => void),
  fieldName?: string
) => {
  e.preventDefault();
  const paste = (e.clipboardData || (window as any).clipboardData).getData("text");
  // Filter out special characters, keeping only alphanumeric, spaces, and basic punctuation
  const filtered = filterMessageSpecialCharacters(paste);
  const textarea = e.target as HTMLTextAreaElement | HTMLInputElement;
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const currentValue = textarea.value;
  const newValue = currentValue.substring(0, start) + filtered + currentValue.substring(end);

  if (setValue && fieldName) {
    // For react-hook-form: setValue(name, value)
    (setValue as (name: string, value: string) => void)(fieldName, newValue);
  } else if (setValue) {
    // For direct value setter: setValue(value)
    (setValue as (value: string) => void)(newValue);
  } else {
    // Fallback: directly set the value
    textarea.value = newValue;
    const event = new Event("input", { bubbles: true });
    textarea.dispatchEvent(event);
  }

  // Set cursor position after pasted content
  setTimeout(() => {
    textarea.setSelectionRange(start + filtered.length, start + filtered.length);
  }, 0);
};

/**
 * Parses an accounting value string and returns the numeric value. i.e. "($100.00)" -> -100
 * @param value - The accounting value string to parse
 * @returns The numeric value of the accounting value
 */
export const parseAccountingValue = (value: string): number => {
  if (!value) return 0;
  const trimmed = value.trim();
  const isNegative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const cleanValue = trimmed.replace(/[^0-9.-]+/g, "");
  const numValue = parseFloat(cleanValue) || 0;
  return isNegative ? -Math.abs(numValue) : numValue;
};

export const parseMessageTemplate = (
  content: string,
  data: Record<string, string | undefined | null>
): string => {
  let result = content;
  Object.keys(data).forEach((key) => {
    result = result.replaceAll(`{{${key}}}`, data[key] ?? "");
    result = result.replaceAll(`{${key}}`, data[key] ?? "");
  });
  return result;
};

export const stripAttachmentBase64 = (input: string): string => {
  const idx = input.indexOf("base64,");
  if (input.startsWith("data:") && idx !== -1) {
    return input.slice(idx + 7).replace(/\s/g, "");
  }
  return input.replace(/\s/g, "");
};
